<template>
  <div class="flex flex-col w-full overflow-y-scroll h-full">
    <div
      v-if="loading"
      class="flex flex-col w-full justify-center items-center h-[90vh]">
      <loader />
    </div>
    <div v-else-if="hasReviews && !loading" class="flex flex-col md:flex-row gap-4 w-full h-full">
      <div class="flex flex-col gap-4 w-full">
        <t-card class="flex flex-col w-full">
          <section class="text-3xl font-medium text-gray-600 flex gap-2 justify-center items-center">
            <i class="pi pi-star-fill w-4 !text-xl" />
            <span>{{ reviewsSummary.averageRating }}</span>
          </section>
          <span class="text-gray-600 text-center my-1 text-sm">סה״כ {{ reviewsSummary.totalReviews }} דירוגים</span>
          <div v-for="reaction of <TReactions[]>['good','ok', 'not-good']" :key="reaction"
               class="flex justify-center items-center w-full gap-2 text-xs text-gray-600 mb-2">
          <span class="border border-gray-200 rounded py-1 px-1.5 flex-none w-16 text-center">{{
              rankMapping[reaction]
            }}</span>
            <div class="w-full">
              <ProgressBar :value="reviewsSummary.reactionsBreakdown[reaction].percentage"
                           :class="['!h-3 rounded-full', reaction]"
                           :show-value="false" />
            </div>
            <span class="flex-grow">({{ reviewsSummary.reactionsBreakdown[reaction].count }})</span>
          </div>
        </t-card>
        <t-card class="flex flex-col w-full">
          <div
            v-if="shouldShowClientsChips"
            class="flex w-full flex-col pb-2">
            <span class="font-medium">סינון לפי עובד</span>
            <div class="flex w-full h-14 justify-start items-center gap-2 overflow-x-auto">
              <multiple-chip-selection :selected-item="filters.providerId"
                                       :items="providers"
                                       @selected="(providerId) => {filters.providerId = providerId}" />

            </div>
          </div>
          <span class="font-medium mb-1">סינון לפי דירוג</span>
          <div class="flex w-full justify-start items-center gap-3 overflow-x-auto">
            <chip v-for="reaction of <TReactions[]>['good','ok', 'not-good']" :key="reaction"
                  :label="rankMapping[reaction]" unstyled
                  class="py-1 rounded-lg text-sm px-3 w-max mt-1 flex gap-2 items-center border border-white cursor-pointer"
                  :class="{
                  'bg-red-100 text-red-800': reaction === 'not-good',
                  'bg-green-100 text-green-800': reaction === 'good',
                  'bg-blue-100 text-blue-800': reaction === 'ok',
                  'border-red-600' : filters.rank ===  'not-good' && reaction === 'not-good',
                  'border-green-600' :  filters.rank ===  'good' && reaction === 'good',
                  'border-blue-600' :  filters.rank === 'ok' &&  reaction === 'ok',
                }"
                  @click="filters.rank = reaction"
            />
          </div>
        </t-card>
      </div>
      <t-card class="flex flex-col w-full md:h-max">
        <transition-group
          mode="out-in"
          name="list"
          class="w-full h-full flex flex-col flex-grow gap-4"
          tag="div"
        >
          <div v-for="(review, index) of reviews"
               class="flex w-full"
               :class="{
                  'border-b pb-4': index !== reviews.length - 1
               }"
               :key="review.appointmentId">
            <div class="flex flex-col w-full">
              <div class="flex justify-between items-center pe-3">
                <span class="font-medium">{{ review.customerName }}</span>
                <chip :label="rankMapping[review.rank]" unstyled class="py-1 rounded-lg text-xs px-1.5 w-max mt-1"
                      :class="{
              'bg-red-100 text-red-800': review.rank === 'not-good',
              'bg-green-100 text-green-800': review.rank === 'good',
              'bg-blue-100 text-blue-800': review.rank === 'ok',
            }" />
              </div>
              <span class="text-xs text-gray-800">
                <span>{{ DateTime.fromISO(review.appointmentDate).toLocaleString(DateTime.DATETIME_MED) }}</span>
              <span v-if="providers.length > 1"> ע״י {{ providersMapping[review.providerId] }}</span>
              </span>
              <span class="text-gray-600 mt-3 text-sm">{{ review.review }}</span>
            </div>
            <Button plain outlined icon="pi pi-arrow-left" :pt="{icon: {class: '!text-gray-600'}}"
                    @click="goToAppointment(review.appointmentId, review.customerId)" />
          </div>
        </transition-group>
        <Button v-if="hasMore" text plain class="mt-2" label="הצג עוד" @click="getReviews(false)" />
        <span class="text-center" v-if="reviews.length === 0">אין עדיין ביקורות</span>
      </t-card>
      <div class="h-2 flex-none"></div>
    </div>
    <div v-else class="w-full h-max flex flex-col justify-center items-center my-auto">
      <img
        src="../../../../../assets/img/admin/no-reviews.svg"
        class="w-32 mb-6"
        alt="אין לקוחות">
      <span class="text-lg font-medium">עוד אין ביקורות</span>
      <span
        class="text-center px-4 font-light">הלקוחות שלך עדיין לא הוסיפו ביקורות. וודאו שהפעלת את פיצ׳ר המשוב האוטומטי שלנו:<br /> עוד ← אוטומציות ← בקשת משוב.</span>
      <div class="flex justify-around">
        <div
          class="relative py-2 px-4 border border-solid m-2 bg-primary-500 text-white rounded-3xl border-purple-800 flex gap-2 justify-center items-center mt-4"
          @click="goTo(AdminRouteNames.Communication)"
        >
          <i class="pi pi-user-plus" />
          <span class="text-sm">מעבר לאוטומציות</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ReviewsApi } from '@app/api/admin';
import { computed, onMounted, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { IReviewsResponse, IReviewsSummary, rankMapping, TReactions } from '@torim-app/types';
import { DateTime } from 'luxon';
import { useAdminStore } from '@app/stores';
import noPhotoData from '@assets/img/admin/no-photo.jpg';
import { AdminRouteNames } from '@app/router/route-names';
import { useRouter } from 'vue-router';
import { useNavigation } from '@app/composables/navigation';
import MultipleChipSelection from '@app/components/admin/components/MultipleChipSelection.vue';

const noPhoto = noPhotoData;
const toast = useToast();
const adminStore = useAdminStore();
const router = useRouter();
const { goTo } = useNavigation();

const emit = defineEmits<{ 'total-changed': [total: number] }>();

const loading = ref<boolean>(true);
const hasReviews = ref<boolean>(false);
const reviews = ref<IReviewsResponse['reviews']>([]);
const skip = ref<number>(0);
const hasMore = ref<boolean>(false);
const reviewsSummary = ref<IReviewsSummary>({} as IReviewsSummary);
const filters = ref<{ providerId: string; rank: TReactions | undefined }>({ providerId: 'any', rank: undefined });

const providersMapping = computed<Record<string, string>>(() => {
  return Object.fromEntries(adminStore.getProviders.map(x => [x.userId, x.fullName]));
});
const isMainClient = computed(() => adminStore.getMainClient);
const canManageOthers = computed(() => adminStore.getCanManageOthers);
const shouldShowClientsChips = computed(() => canManageOthers.value && providers.value.length > 1);
const providers = computed<Array<{ label: string, value: string; image?: string }>>(() => canManageOthers.value ? [{
  value: 'any',
  label: 'ללא',
}, ... adminStore.getProviders.map(x => ({
  label: x.fullName,
  value: x.userId,
  image: x.profileImage ?? noPhoto
}))] : []);

const getReviews = async (hasFilter: boolean) => {
  try {
    const res = await ReviewsApi.fetchReviews({
      skip: skip.value,
      providerId: filters.value.providerId !== 'any' ? filters.value.providerId : undefined,
      rank: filters.value.rank
    });
    if (hasFilter)
      reviews.value.splice(0);

    reviews.value.push(...res.reviews);
    hasMore.value = res.pagination.hasMore;
    skip.value = reviews.value.length;
  } catch (e) {
    console.log(e);
    toast.error('לא הצלחנו לטעון את הביקורות');
  }
};

const getReviewsSummary = async () => {
  try {
    reviewsSummary.value = await ReviewsApi.fetchReviewsSummary(filters.value.providerId !== 'any' ? filters.value.providerId : undefined);
    emit('total-changed', reviewsSummary.value?.totalReviews ?? 0);
  } catch (e) {
    console.log(e);
    toast.error('לא הצלחנו לטעון את הביקורות');
  }
};

const goToAppointment = (apid: string, customerId: string) => {
  router.push({
    name: AdminRouteNames.ClientProfile,
    params: { id: customerId },
    query: { apid }
  });
};

onMounted(() => {
  Promise.all([
    getReviews(false),
    getReviewsSummary()
  ]).then(() => {
    hasReviews.value = reviews.value.length > 0;
    loading.value = false;
  });
});

watch(filters, () => {
  Promise.all([
    getReviews(true),
    getReviewsSummary()
  ]);
}, { deep: true });

</script>

<style lang="scss">
.not-good {
  .p-progressbar-value {
    background: #e00f0f !important;
  }
}
</style>
